import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { AuthMockApi } from 'app/mock-api/common/auth/api';
import { TFMService } from 'app/mock-api/tfm.service';
import { catchError, Observable, of, ReplaySubject, switchMap, throwError } from 'rxjs';

@Injectable({providedIn: 'root'})
export class AuthService
{
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _tfmService = inject(TFMService);
    private _authMockAPI = inject(AuthMockApi);

    constructor(@Inject(PLATFORM_ID) private platformId) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        if (isPlatformBrowser(this.platformId)) {
            return localStorage.getItem('accessToken') ?? '';
        }

        return '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }, options:any = {}): Observable<any>
    {
        var rs = new ReplaySubject();

        // Throw error, if the user is already logged in
        // if ( this._authenticated )
        // {
        //     return throwError('User is already logged in.');
        // }

        // return this._httpClient.post('api/auth/sign-in', credentials).pipe(
        //     switchMap((response: any) =>
        //     {
        //         // Store the access token in the local storage
        //         this.accessToken = response.accessToken;

        //         // Set the authenticated flag to true
        //         this._authenticated = true;

        //         // Store the user on the user service
        //         this._userService.user = response.user;

        //         // Return a new observable with the response
        //         return of(response);
        //     }),
        // );
        
        this._tfmService.getVersion(true).subscribe({
            next: (res) => {
                var linkLogin = this._tfmService.getLink(res._links, "agent-login");

                var content = {
                    username: options.isAutoLogin ? options.username : credentials.email,
                    password: options.isAutoLogin ? '' : credentials.password,
                    source: options.isAutoLogin ? options.source : ''
                };

                this._tfmService.send(linkLogin, content).subscribe({
                    next: (loginRes) => {
                        if (loginRes && loginRes.id_token) {
                            let data = loginRes;

                            if (localStorage) {
                                localStorage.setItem('id_token', data.id_token);
                                localStorage.setItem('agentGuid', data.agent.GUID);
                                localStorage.setItem('agentId', data.agent.Id);

                                if (data.agent.AgencyID && !localStorage.getItem('agencyID'))
                                    localStorage.setItem('agencyID', data.agent.AgencyID);
                                if (data.agent.SiteID && !localStorage.getItem('siteID'))
                                    localStorage.setItem('siteID', data.agent.SiteID);
                                if (data.agent.SiteGUID && data.agent.SiteGUID != '00000000-0000-0000-0000-000000000000' && !localStorage.getItem('siteGUID'))
                                    localStorage.setItem('siteGUID', data.agent.SiteGUID);
                            }

                            this.accessToken = this._authMockAPI._generateJWTToken();
                            this._authenticated = true;
                            this._userService.user = loginRes;

                            rs.next(loginRes);
                            rs.complete();
                        }
                        else {
                            rs.error(new Error('Invalid credentials'));
                        }
                    },
                    error: (err) => {
                        rs.error(err);
                    }
                });
            },
            error: (err) => {
                rs.error(err);
            }
        });

        return rs;
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: any) =>
            {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if ( response.accessToken )
                {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
